<template>
  <div>
    <apexchart type="line" :options="options" :series="seriesData" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import _ from 'lodash'
import moment from 'moment'
import { type ApexOptions } from 'apexcharts'
import { SensorSummary, TriggerSummary } from '@/models/sensors-dto'
import { useSensorReadingsChartData } from '@/api/sensors'
import { useStore } from 'vuex-composition-helpers'

const store = useStore()

const props = defineProps<{
  sensor: SensorSummary
  trigger?: TriggerSummary
  showAggregate?: boolean
  showJobsFlags?: boolean
}>()

const sensorIds = computed(() => {
  return props.trigger != null ? props.trigger.sensorIds : [props.sensor.id]
})

const sensorRef = toRef(props, 'sensor')
const triggerRef = toRef(props, 'trigger')
const showAggregateRef = toRef(props, 'showAggregate')
const showJobsFlagsRef = toRef(props, 'showJobsFlags')
const windowStart = moment().subtract(1, 'week').toDate()
const windowEnd = moment().add(1, 'week').toDate()

const scadaUnits = computed<string>(() => store.state.organizationConfig.scadaUnits)

const options = computed(() => {
  const maxYValue = showAggregateRef.value
    ? _.sum(chartData.value?.tankProperties.map((t) => t.max))
    : sensorRef.value.maxValue * sensorRef.value.factor

  const xMaxValue =
    triggerRef.value !== null
      ? moment().add(triggerRef.value?.lookahead, 'days').toDate()
      : windowEnd

  const jobAnnotations = showJobsFlagsRef.value
    ? triggerRef.value?.events
        .filter((e) => e.type === 'Job')
        .map((e) => {
          const annotation = {
            label: {
              text: `Job #${e.data.jobNumber}`,
              style: {
                background: '#d9e1f4',
              },
            },
            x: new Date(e.data.time).getTime(),
            strokeDashArray: 0,
            borderColor: '#0040ff',
          }
          return annotation
        })
    : []

  const thresholdAnnotation = triggerRef.value && {
    label: { text: `Threshold` },
    y: showAggregateRef.value
      ? triggerRef.value?.threshold
      : triggerRef.value?.threshold / triggerRef.value.sensorIds.length,
    strokeDashArray: 2,
    borderColor: '#aaa',
    fillColor: '#ddd',
  }

  const loadLineAnnotation = sensorRef.value && {
    label: { text: `Load Line` },
    y: showAggregateRef.value
      ? sensorRef.value?.minValue * sensorRef.value?.factor * sensorIds.value.length
      : sensorRef.value?.minValue * sensorRef.value?.factor,
    strokeDashArray: 2,
    borderColor: '#aaa',
    fillColor: '#ddd',
  }

  const o: ApexOptions = {
    chart: {
      id: 'vuechart-example',
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    grid: { show: false },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    xaxis: { min: windowStart.getTime(), max: xMaxValue.getTime(), type: 'datetime' },
    yaxis: {
      min: 0,
      max: maxYValue,
      title: { text: `Tank Level (${scadaUnits.value})` },
      decimalsInFloat: 0,
    },
    annotations: {
      xaxis: [...(jobAnnotations || [])],
      yaxis: [thresholdAnnotation, loadLineAnnotation].filter((a) => a !== undefined) as [],
    },
  }

  return o
})

const { data: chartData, isLoading } = useSensorReadingsChartData(sensorIds, windowStart, windowEnd)

const seriesData = computed(() => {
  if (showAggregateRef.value) {
    const readingsPerTimeBucket = _.groupBy(chartData.value?.readings ?? [], (r) => r.date)

    return [
      {
        name: 'Aggregate Value',
        data: Object.keys(readingsPerTimeBucket).map((key) => ({
          x: new Date(key),
          y: _.sum(readingsPerTimeBucket[key].map((r) => r.value)),
        })),
      },
    ]
  } else {
    const allReadings = _.groupBy(chartData.value?.readings ?? [], (r) => r.deviceId)

    return _.sortBy(
      Object.keys(allReadings).map((key) => {
        return {
          name: key,
          data: allReadings[key].map((d) => ({ x: new Date(d.date), y: d.value })),
        }
      }),
      (x) => x.name,
    )
  }
})
</script>

<style scoped></style>
